<template>
    <div class="public-road-access-app">
        <loading
            :active="loading_pra"
            :can-cancel="false"
            :is-full-page="false"
        />

        <div v-if="!pra" class="panel panel-default text-center">
            <div class="panel-heading">
                {{ $t("analysis:tabs:public_road_access") }}
            </div>
            <div class="panel-wrapper collapse in">
                <div class="panel-body">
                    <div class="row" v-if="failed">
                        <p>{{ $t("amas:analysis:pra:access_analysis_failed") }}</p>
                        <button type="button" class="btn btn-info btn-outline" v-on:click="backendRecreate()">
                            {{ $t("general:re_collect") }}
                        </button>
                    </div>

                    <div class="row" v-else>
                        <p>{{ $t("amas:analysis:pra:getting_access") }}</p>
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="panel panel-default">
            <div class="panel-wrapper collapse in">
                <div class="panel-body">
                    <status-header
                        :result="pra.result"
                        :save-state="save_state"
                        :saved-at="saved_at"
                        :manual-step-count="manualSteps.length + Object.keys(overrides).length"
                        :last-updated="pra.last_updated"
                        :verdicts-follow-edits="!!(pra.verdicts && pra.verdicts.from_edits)"
                        :verdicts-need-recalculation="!!(pra.verdicts && pra.verdicts.needs_recalculation)"
                        @recalculate="backendRecreate()"
                    />

                    <div class="row">
                        <div class="col-md-7">
                            <verdict-cards :result="pra.result" :verdicts="pra.verdicts" @change="setVerdict" />

                            <report-note
                                :value="pra.result.comment || ''"
                                :generated="pra.generated_description || ''"
                                :loading="note_loading"
                                @input="setComment"
                                @generate="generateDescription"
                            />
                        </div>

                        <div class="col-md-5">
                            <h4 class="m-t-0">{{ $t("amas:analysis:pra:map") }}</h4>
                            <k-wms-leaflet-map
                                :polygons="roadAccessPolygons"
                                :center="center"
                                :legend="legend"
                                @polygon-mouseover="highlightItem($event)"
                                @polygon-mouseout="unhighlightItem($event)"
                                @click="handleMapClick"
                                :disable-auto-fit="disableAutoFit"
                                :fullscreen="true"
                                :zoom="18"
                                :max-zoom="20"
                                :height="330"
                            >

                                <template v-slot:markers>
                                    <l-marker
                                        v-if="clicked_marker"
                                        :lat-lng="clicked_marker.gps_point.coordinates"
                                        :icon="blankMarkerIcon"
                                        ref="clickmarker"
                                    >
                                        <l-popup>
                                            <popup-table
                                                :entry="(clicked_marker.parcel && entriesById[clicked_marker.parcel.property_id]) || null"
                                                :parcel="clicked_marker.parcel"
                                                :property_id="clicked_marker.parcel && clicked_marker.parcel.property_id"
                                                :can-add="clicked_marker.parcel && canAdd(clicked_marker.parcel.property_id)"
                                                :can-remove="clicked_marker.parcel && canRemove(clicked_marker.parcel.property_id)"
                                                @add_step="addStep"
                                                @remove_step="removeStep"
                                                @override="setOverride"
                                            />
                                        </l-popup>
                                    </l-marker>
                                    <l-polygon
                                        v-if="clickedParcelPolygon"
                                        :lat-lngs="clickedParcelPolygon.coordinates"
                                        :fill-color="clickedParcelPolygon.style.color"
                                        :stroke="clickedParcelPolygon.style.stroke"
                                        :color="clickedParcelPolygon.style.color"
                                        :weight="clickedParcelPolygon.style.weight"
                                        :fill-opacity="clickedParcelPolygon.style.fillOpacity"
                                    />
                                </template>

                                <template v-slot:popup_content_polygon="slotProps">
                                    <popup-table
                                        :entry="entriesById[slotProps.polygon.property_id] || null"
                                        :parcel="null"
                                        :property_id="slotProps.polygon.property_id"
                                        :can-add="canAdd(slotProps.polygon.property_id)"
                                        :can-remove="canRemove(slotProps.polygon.property_id)"
                                        @add_step="addStep"
                                        @remove_step="removeStep"
                                        @override="setOverride"
                                    />
                                </template>

                            </k-wms-leaflet-map>
                        </div>
                    </div>

                    <trace-table
                        :entries="entries"
                        :has-trace="hasTrace"
                        :highlighted-property-id="highlighted_property_id"
                        :zk-loading="zk_loading"
                        :pending="route_pending"
                        @highlight="highlighted_property_id = $event"
                        @remove-step="removeStep"
                        @zk-extract="openZkExtract"
                        @override="setOverride"
                    />
                    <div class="m-t-10">
                        <div class="font-medium m-b-5">{{ $t("amas:analysis:pra:add_manual_step") }}</div>
                        <add-rekeys-textarea
                            @submit="addSteps"
                            :rows="1"
                        />
                    </div>
                </div>
            </div>

        </div>

        <notifications
            group="warn"
        />
    </div>
</template>

<script>
import L from "leaflet";
import { icon } from "leaflet";
import { LMarker, LPopup, LPolygon } from "vue2-leaflet";

import ApiService from "@/components/utils/api.service.js"
import ApiCalls from "@/components/utils/api.calls.js"
import { Swal } from "@/components/utils/swal.alerts.js";
import KWMSLeafletMap from "@/components/k-leaflet-map/KWMSLeafletMap"

import AddRekeysTextarea from "@apps/analysis/project/vue/valuation_subject_wizard/components/AddRekeysTextarea.vue"

import StatusHeader from "./components/StatusHeader.vue";
import VerdictCards from "./components/VerdictCards.vue";
import ReportNote from "./components/ReportNote.vue";
import TraceTable from "./components/TraceTable.vue";
import PopupTable from "./components/PopupTable.vue";
import { stateOf, polygonStyle, legendEntries } from "./states.js";

export default {
    props: {
        session_id: {
            type: Number,
            required: true
        }
    },
    components: {
        StatusHeader,
        VerdictCards,
        ReportNote,
        TraceTable,
        AddRekeysTextarea,
        PopupTable,
        "k-wms-leaflet-map": KWMSLeafletMap,
        LMarker, LPopup, LPolygon
    },
    data: function () {
        return {
            pra: null,
            loading_pra: false,
            failed: false,

            save_state: "idle",      // idle | saving | saved | error
            route_pending: false,    // a parcel edit waits for the server's route
            saved_at: null,
            save_timer: null,
            save_seq: 0,
            suppress_autosave: false,

            highlighted_property_id: null,
            clicked_marker: null,
            original_source_parcels: [],
            zk_loading: {},
            note_loading: false,

            // API urls
            pra_url: "/amas/analysis/analysis/api/" + this.session_id + "/road-access",

            clicked_parcel_polygon_style: {
                color: "#fffdaa",
                edgeColor: "#fffdaa",
                stroke: true,
                weight: 3,
                fillOpacity: 0.5
            },

            isPreviewingParcel: false
        }
    },
    methods: {
        highlightItem: function(property_id) {
            this.highlighted_property_id = property_id;
        },
        unhighlightItem: function(property_id) {
            this.highlighted_property_id = null;
        },
        handleMapClick: async function(e) {
            this.clicked_marker = {
                gps_point: {
                    "type": "Point", "coordinates": [e.latlng.lat, e.latlng.lng]
                },
                parcel: null
            };
            this.isPreviewingParcel = true;
            this.$nextTick(() => {
                if (this.$refs.clickmarker) {
                    this.$refs.clickmarker.mapObject.openPopup();
                }
            });

            ApiCalls
                .getParcelsByGps(e.latlng.lat, e.latlng.lng)
                .then(res => this.clicked_marker.parcel = res[0]);
        },
        backendRecreate: function() {
            const app = this;
            Swal({
                title: app.$t("general:are_you_sure"),
                text: app.$t("amas:analysis:pra:system_will_replace_note:alert"),
                showCancelButton: true,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: app.$t("amas:analysis:pra:yes_update"),
                cancelButtonText: app.$t("general:no_cancel"),
            }).then(function(val){
                if (val.isConfirmed) {
                    app.getPra(true);
                }
            });
        },
        getPra: function(force_update=false) {
            const app = this;
            app.loading_pra = true;
            clearTimeout(app.save_timer);
            app.save_timer = null;
            app.save_seq++;
            return ApiService
                .get(this.pra_url, { params: { force_update: force_update } })
                .then(response => {
                    app.loading_pra = false;
                    if (!response.data.result) {
                        // A row without a result (an interrupted run): offer the re-collect button.
                        app.pra = null;
                        app.failed = true;
                        return response.data;
                    }
                    app.suppress_autosave = true;
                    app.pra = response.data;
                    app.route_pending = false;
                    app.failed = false;
                    app.save_state = "idle";
                    app.original_source_parcels = this.pra.result.source_parcels.map(parcel => parcel.property_id);
                    // Rows stored before the has_legal_access -> has_access rename.
                    if (app.pra.result && app.pra.result.has_access === undefined) {
                        app.$set(app.pra.result, "has_access", !!app.pra.result.has_legal_access);
                    }
                    // Road parcel last so the route reads subject -> ... -> road.
                    if (app.pra.result && Array.isArray(app.pra.result.access_path)) {
                        const path = app.pra.result.access_path;
                        app.pra.result.access_path = path.filter(p => !p.has_public_road).concat(path.filter(p => p.has_public_road));
                    }
                    app.$nextTick(() => { app.suppress_autosave = false; });
                    return response.data;
                })
                .catch(error => {
                    app.loading_pra = false;
                    if (!error.response) {
                        app.failed = true;
                        throw error;
                    }
                    if(error.response.status===404) {
                        Swal({
                            title: "",
                            text: app.$t("amas:analysis:pra:access_not_yet_obtained:alert"),
                            showCancelButton: true,
                            confirmButtonColor: "#DD6B55",
                            confirmButtonText: app.$t("amas:analysis:pra:confirm_obtain"),
                            cancelButtonText: app.$t("general:no_cancel"),
                        }).then(function(val){
                            if (val.isConfirmed) {
                                app.getPra(true);
                            } else {
                                app.failed = true;
                            }
                        });
                    } else {
                        app.failed = true;
                        Swal({
                            title: app.$t("general:error"),
                            icon: "error",
                            text: (error.response.data && error.response.data.message) || "",
                        });
                        throw error;
                    }
                })
        },
        // Autosave
        scheduleSave: function() {
            if (this.suppress_autosave || this.loading_pra) { return; }
            clearTimeout(this.save_timer);
            this.save_seq++;
            this.save_timer = setTimeout(this.save, 1000);
        },
        flushSave: function() {
            if (this.save_timer) {
                clearTimeout(this.save_timer);
                this.save_timer = null;
                return this.save();
            }
            return Promise.resolve();
        },
        generateDescription: function() {
            // The description comes from the save response, so pending edits are saved first.
            this.note_loading = true;
            this.flushSave()
                .then(() => this.setComment(this.pra.generated_description || ""))
                .then(() => { this.note_loading = false; }, () => { this.note_loading = false; });
        },
        save: function() {
            const app = this;
            const seq = ++app.save_seq;
            app.save_timer = null;
            app.save_state = "saving";
            return ApiService
                .post(this.pra_url, { result: this.pra.result })
                .then(response => {
                    if (seq !== app.save_seq) { return; }
                    app.route_pending = false;
                    app.suppress_autosave = true;
                    app.pra.generated_description = response.data.generated_description;
                    app.$set(app.pra, "verdicts", response.data.verdicts);
                    app.$set(app.pra, "route_states", response.data.route_states);
                    app.$set(app.pra, "manual_depths", response.data.manual_depths);
                    // Manually added parcels name their company owners, which only the server knows.
                    ((response.data.result && response.data.result.access_path) || []).forEach(saved => {
                        const step = app.manualSteps.find(s => s.property_id === saved.property_id);
                        if (step && saved.owners) { app.$set(step, "owners", saved.owners); }
                    });
                    // The server records the borders of manually added parcels; keep them so the next save does not drop them.
                    if (response.data.result && response.data.result.links) { app.$set(app.pra.result, "links", response.data.result.links); }
                    app.pra.image = response.data.image;
                    app.pra.access_path_with_geometry = response.data.access_path_with_geometry;
                    app.$nextTick(() => { app.suppress_autosave = false; });
                    app.save_state = "saved";
                    app.saved_at = new Date();
                })
                .catch(error => {
                    if (seq !== app.save_seq) { return; }
                    app.route_pending = false;
                    app.save_state = "error";
                    app.$notify({
                        group: "warn",
                        type: "warn",
                        title: app.$t("general:error"),
                        text: (error.response && error.response.data) ? error.response.data.message : app.$t("amas:analysis:pra:save_failed"),
                    });
                });
        },
        setComment: function(value) {
            this.$set(this.pra.result, "comment", value);
        },
        setVerdict: function(field, value) {
            this.$set(this.pra.result, field, value);
        },
        setOverride: function(property_id, field, value) {
            const entry = this.traceById[property_id];
            if (!entry) {
                // Manual steps are not traced: their flags live on the step itself, passable unless switched off.
                const step = this.manualSteps.find(s => s.property_id === property_id);
                const current = step && (field === "has_public_road" ? !!step[field] : step[field] !== false);
                if (step && current !== value) {
                    this.route_pending = true;
                    this.$set(step, field, value);
                }
                return;
            }
            this.route_pending = true;
            const modelValues = { has_legal_access: entry.legal.ok, has_physical_access: entry.physical.ok, has_public_road: !!entry.has_public_road };
            const modelValue = modelValues[field];
            const overrides = Object.assign({}, this.overrides);
            const current = Object.assign({}, overrides[property_id] || {});
            if (value === modelValue) {
                delete current[field];
            } else {
                current[field] = value;
            }
            if (Object.keys(current).length) {
                overrides[property_id] = current;
            } else {
                delete overrides[property_id];
            }
            this.$set(this.pra.result, "overrides", overrides);
        },
        notifyLegalError: function(error) {
            this.$notify({
                group: "warn",
                type: "warn",
                title: "Opozorilo",
                text: (error && error.response && error.response.data) ? error.response.data.message : "Napaka pri pridobivanju pravnega stanja",
            });
        },
        openZkExtract: function(property_id) {
            this.$set(this.zk_loading, property_id, true);
            ApiCalls
                .getLegalStatus(property_id)
                .then(legal_status => {
                    this.$set(this.zk_loading, property_id, false);
                    if (legal_status && legal_status.legal_file) {
                        this.openPdf(legal_status.legal_file, legal_status.name || property_id);
                    } else {
                        this.notifyLegalError(null);
                    }
                })
                .catch(error => {
                    this.$set(this.zk_loading, property_id, false);
                    this.notifyLegalError(error);
                });
        },
        openPdf: function(url, title) {
            // Same modal the legal tab uses (custom-view-pdf.js); the examples page has no jQuery modal.
            const $ = window.jQuery || window.$;
            if ($ && $.createModal && window.PDFObject) {
                $.createModal({
                    title: `${title} <a class="btn btn-xs btn-default" href="${url}">${this.$t("general:download:btn")}</a>`,
                    message: '<div id="pdf-view" style="height:940px"></div>',
                    closeButton: true,
                    scrollable: false,
                });
                window.PDFObject.embed(url, "#pdf-view");
            } else {
                window.open(url, "_blank");
            }
        },
        normalizeOwner: function(name) {
            return (name || "").toLowerCase().replace(/\s+/g, "")
                .replace(/š/g, "s").replace(/č/g, "c").replace(/ž/g, "z").replace(/ć/g, "c");
        },
        attachLegalStatus: function(property_id) {
            // The ZK owners only flag a manual step owned like the subject; they are never shown.
            return ApiCalls
                .getLegalStatus(property_id)
                .then(legal_status => {
                    const step = this.accessPathParcels.find(s => s.property_id === property_id);
                    if (!step || !legal_status) { return; }
                    const owners = (legal_status.legal_owners || []).map(o => this.normalizeOwner((o.name || "").split(", ")[0]));
                    const subjectOwners = this.entries.filter(e => e.depth === 0)
                        .reduce((acc, e) => acc.concat((e.owners || []).map(o => this.normalizeOwner(o.name))), []);
                    this.$set(step, "same_owner", owners.some(name => name && subjectOwners.includes(name)));
                })
                .catch(this.notifyLegalError);
        },
        // Manage steps
        addSteps: function(text) {
            text.split(/[\s,;]+/).filter(Boolean).forEach(this.addStep);
        },
        addStep: function(property_id) {
            if( !this.canAdd(property_id) ) {
                alert("Ni mogoče dodati");
                return;
            }

            // Look the parcel up first so an unknown key is never pushed (and autosaved) onto the route.
            return ApiCalls
                .getRealEstateData(property_id)
                .then((real_estate) => {
                    if (!real_estate) {
                        this.$notify({
                            group: "warn",
                            type: "warn",
                            title: this.$t("general:warning"),
                            text: this.$t("amas:analysis:pra:re_was_not_found:{}").replace("{}", property_id),
                        });
                        return;
                    }
                    if (!this.canAdd(property_id)) { return; }
                    this.route_pending = true;
                    this.pra.result.access_path.push(this.createStepData(property_id));
                    if (this.clicked_marker && this.clicked_marker.parcel && this.clicked_marker.parcel.property_id === property_id) {
                        this.addGeometryToParcelLists(this.clicked_marker.parcel);
                    }
                    this.attachLegalStatus(property_id);
                })
                .catch(() => {
                    this.$notify({
                        group: "warn",
                        type: "error",
                        title: this.$t("general:warning"),
                        text: this.$t("amas:analysis:pra:re_was_not_found:{}").replace("{}", property_id),
                    });
                });
        },
        removeStep: function(property_id) {
            if( !this.canRemove(property_id) ) {
                alert("Ni mogoče odstraniti");
                return;
            }

            this.route_pending = true;
            this.pra.result.access_path = this.pra.result.access_path.filter(parcel => parcel.property_id !== property_id);
            this.removeFromGeometryArray("access_path_with_geometry", property_id);
        },
        canAdd: function(property_id) {
            return !this.allPropertyIDs.includes(property_id) && !this.entriesById[property_id];
        },
        canRemove: function(property_id) {
            return this.manualSteps.some(step => step.property_id === property_id);
        },
        isInOriginalParcels: function(property_id) {
            return this.original_source_parcels.includes(property_id)
        },
        createStepData: function(property_id) {
            return {
                property_id: property_id,
                has_public_road: false,
                has_legal_access: true,
                has_physical_access: true,
                manual: true,
            };
        },

        ensureGeometryArrayExists: function(arrayKey) {
            if (!this.pra[arrayKey]) {
                this.$set(this.pra, arrayKey, []);
            }
        },
        addToGeometryArray: function(arrayKey, parcelData) {
            const exists = this.pra[arrayKey].some(p => p.property_id === parcelData.property_id);
            if (!exists) {
                this.pra[arrayKey].push(parcelData);
            }
        },
        removeFromGeometryArray: function(arrayKey, property_id) {
            if (this.pra[arrayKey]) {
                this.pra[arrayKey] = this.pra[arrayKey].filter(parcel => parcel.property_id !== property_id);
            }
        },

        addGeometryToParcelLists: function(parcel) {
            const parcelData = {
                id: parcel.id || 0,
                property_id: parcel.property_id,
                units_list: parcel.units_list || [],
                geom: parcel.geom,
                geom_swapped: parcel.geom_swapped
            }

            const arrayKey = this.isInOriginalParcels(parcel.property_id) ? "source_parcels_with_geometry" : "access_path_with_geometry";
            this.ensureGeometryArrayExists(arrayKey);
            this.addToGeometryArray(arrayKey, parcelData);
        }
    },
    mounted() {
        window.addEventListener("beforeunload", this.flushSave);
        this.getPra();
    },
    beforeDestroy() {
        window.removeEventListener("beforeunload", this.flushSave);
        this.flushSave();
    },
    computed: {
        hasTrace() { return !!(this.pra && this.pra.result && Array.isArray(this.pra.result.trace)); },
        sourceParcels() { return (this.pra && this.pra.result && this.pra.result.source_parcels) || []; },
        accessPathParcels() { return (this.pra && this.pra.result && this.pra.result.access_path) || []; },
        manualSteps() { return this.accessPathParcels.filter(step => step.manual); },
        allPropertyIDs() { return this.sourceParcels.concat(this.accessPathParcels).map(s => s.property_id); },
        legacyEntries() {
            // Results stored before the trace existed: rebuild rows from the two step lists.
            const fromStep = (step, depth) => ({
                property_id: step.property_id, depth: depth, via_property_id: null,
                has_public_road: !!step.has_public_road,
                legal: { ok: !!step.has_legal_access, reason: null, easement: null },
                physical: { ok: !!step.has_physical_access, border_length: null },
                same_owner: false, owners: [],
            });
            return this.sourceParcels.map(s => fromStep(s, 0))
                .concat(this.accessPathParcels.filter(s => !s.manual).map(s => fromStep(s, 1)));
        },
        overrides() { return (this.pra && this.pra.result && this.pra.result.overrides) || {}; },
        traceById() {
            const map = {};
            (this.hasTrace ? this.pra.result.trace : this.legacyEntries).forEach(e => { map[e.property_id] = e; });
            return map;
        },
        entries() {
            const manualIds = this.manualSteps.map(step => step.property_id);
            const subjectIds = this.pra.subject_parcel_ids;
            const base = (this.hasTrace ? this.pra.result.trace : this.legacyEntries).map(entry => {
                // A starting parcel outside the analysis is the land under the building, not the subject.
                if (entry.depth === 0 && Array.isArray(subjectIds) && !subjectIds.includes(entry.property_id)) {
                    entry = Object.assign({}, entry, { building_land: true });
                }
                // A manual step on a traced parcel (older data) reads as "both conditions overridden to yes".
                const override = this.overrides[entry.property_id]
                    || (manualIds.includes(entry.property_id) ? { has_legal_access: true, has_physical_access: true } : null);
                if (!override) { return entry; }
                const pick = (value, fallback) => (value === undefined || value === null) ? fallback : !!value;
                const hasRoad = pick(override.has_public_road, entry.has_public_road);
                return Object.assign({}, entry, {
                    overridden: true,
                    has_public_road: hasRoad,
                    // A public road satisfies the legal condition on its own.
                    legal: Object.assign({}, entry.legal, { ok: pick(override.has_legal_access, entry.legal.ok || hasRoad) }),
                    physical: Object.assign({}, entry.physical, { ok: pick(override.has_physical_access, entry.physical.ok) }),
                });
            });
            // A manual parcel joins the step it borders; one bordering nothing checked has no step.
            const manualDepths = this.pra.manual_depths || {};
            const manual = this.manualSteps.filter(step => !this.traceById[step.property_id]).map(step => ({
                property_id: step.property_id, depth: manualDepths[step.property_id] || null, via_property_id: null, manual: true,
                has_public_road: !!step.has_public_road,
                legal: { ok: step.has_legal_access !== false, reason: "manual", easement: null },
                physical: { ok: step.has_physical_access !== false, border_length: null },
                same_owner: !!step.same_owner, owners: step.owners || [],
            }));
            const routeStates = this.pra.route_states || {};
            return base.concat(manual).map(entry => Object.assign({}, entry, { state: stateOf(entry, routeStates) }));
        },
        entriesById() {
            const map = {};
            this.entries.forEach(e => { map[e.property_id] = e; });
            return map;
        },
        // Map polygons
        roadAccessPolygons() {
            if (!this.pra) { return []; }
            const seen = {};
            const polygons = [];
            ["trace_with_geometry", "source_parcels_with_geometry", "access_path_with_geometry"].forEach(key => {
                (this.pra[key] || []).forEach(parcel => {
                    if (!parcel.geom_swapped || seen[parcel.property_id]) { return; }
                    seen[parcel.property_id] = true;
                    const entry = this.entriesById[parcel.property_id] || null;
                    polygons.push({
                        ...parcel.geom_swapped,
                        property_id: parcel.property_id,
                        style: polygonStyle(entry ? entry.state : "off_route"),
                        entry: entry,
                        // Fit the view to the subject and its direct neighbours only.
                        excludeFromFit: !entry || entry.manual || entry.depth > 1,
                    });
                });
            });
            // Route parcels last so they draw on top.
            const onRoute = polygon => (polygon.entry && polygon.entry.state !== "off_route") ? 1 : 0;
            return polygons.sort((a, b) => onRoute(a) - onRoute(b));
        },
        clickedParcelPolygon: function() {
            if(this.clicked_marker && this.clicked_marker.parcel) {
                let parcel = this.clicked_marker.parcel;
                return {
                    ...parcel.geom_swapped,
                    ...{
                        style: this.clicked_parcel_polygon_style,
                        parcel: parcel,
                        property_id: parcel.property_id
                    }
                }
            }
            return null;
        },
        blankMarkerIcon: function() {
            // This is a blank marker icon!
            // It's here so that the parcel details work as intended, don't remove.
            var svg = ""
            var iconUrl = "data:image/svg+xml;base64," + btoa(svg);
            return icon({
                iconUrl: iconUrl,
            })
        },
        // Map center and legend
        center() {
            let center;
            if(this.pra && this.pra.map_center) {
                center = [this.pra.map_center[1], this.pra.map_center[0]]
            }
            return center;
        },
        legend () {
            const app = this;
            var legend = L.control({position: 'bottomleft'});
            legend.onAdd = function (leafletmap) {

                let roadAccessLegendDiv = L.DomUtil.create('div', 'leaflet-legend pra-leaflet-legend');
                roadAccessLegendDiv.setAttribute('hidden', true);

                legendEntries(key => app.$t(key), app.entries.some(e => e.building_land)).forEach(function(item){
                    roadAccessLegendDiv.innerHTML += `
                    <div class="m-b-5">
                        <svg class="leaflet-icon" width="20" height="20">
                            <rect width="20" height="20" style="fill:${item.color};"/>
                        </svg>
                        <strong style="color:white;"> - ${item.label}</strong>
                    </div>
                    `
                })

                // Toggle legend button
                let toggleButton = L.DomUtil.create('button', 'btn btn-xs')
                toggleButton.innerHTML += app.$t("maps:legend")
                L.DomEvent.addListener(toggleButton, 'click', function () {
                    roadAccessLegendDiv.toggleAttribute('hidden');
                });

                let div = L.DomUtil.create('div', 'm-0');
                div.appendChild(toggleButton);
                div.appendChild(roadAccessLegendDiv);

                return div;
            };

            return legend;

        },
        disableAutoFit: function() {
            return this.isPreviewingParcel;
        }
    },
    watch: {
        "clicked_marker.parcel": function(parcel) {
            if (parcel === undefined && this.$refs.clickmarker) {
                this.$refs.clickmarker.mapObject.closePopup();
                this.isPreviewingParcel = false;
            }
        },
        "pra.result": {
            handler: function() {
                this.scheduleSave();
                // Force popup to update when PRA data changes
                if (this.$refs.clickmarker && this.clicked_marker && this.clicked_marker.parcel) {
                    this.$nextTick(() => {
                        if (this.$refs.clickmarker.mapObject._popup && this.$refs.clickmarker.mapObject._popup.isOpen()) {
                            this.$refs.clickmarker.mapObject._popup.update();
                        }
                    });
                }
            },
            deep: true
        }
    }
}
</script>

<style>
.pra-leaflet-legend {
    background: rgba(0, 0, 0, 0.5);
    padding: 5px;
}
.pra-leaflet-legend .leaflet-icon {
    vertical-align: middle;
}

.public-road-access-app .dropdown-menu {
    padding: 0;
}

.public-road-access-app  .leaflet-popup-content {
    width: 300px!important;
    margin: 0px;
}
</style>
